// Hard Mode: the run's constitution (§26 F13).
//
// **One picker, two dials.** What a player chooses is the easy/normal/hard
// difficulty that has always been there; picking hard sets both the skill
// ceiling the run climbs to (`levelForStage`) and the mode here. There is no
// second menu item, and easy already does double duty in exactly this way —
// it halves the pacing curve as well as picking a skill.
//
// Internally the two stay apart, because they are different kinds of thing. A
// `Level` is a *per-camp* skill that **climbs** as the run goes on and is set
// per side by a scenario; hanging run-structure constants off it would switch
// Hard Mode's rules on halfway through a run and leak them into scenarios that
// merely wanted a strong opponent. The mode is a run-wide constant, derived
// once when the run opens, and its three numbers are **in force from turn
// one** — the mode is the run's constitution, the climb is the opponents'
// competence.
//
// **This file is the one reading.** Every caller — the surrender gate, the
// despair banner, `despairStep`, `armSpawnClock`, `buildWave` — goes through a
// helper here, so nothing can disagree about what hard means; the despair
// banner's rule ("one subtraction, three readers"), applied to a mode.

import type { RunState } from "../commands";
import { RULES } from "../rules";

/**
 * Which game the run is playing.
 *
 * Two values and no more: a dial with a vocabulary is refused rather than
 * defaulted on the way in (`normalizeRunMode`), the map's rule.
 */
export type RunMode = "normal" | "hard";

/**
 * What the run is, defaulting to normal.
 *
 * Absent is normal, everywhere, and the field is only ever *stored* when hard
 * (`RunState.mode`) — so every run that never chose reads and hashes exactly
 * as it did before this existed. A run that is not there at all is normal too:
 * a scenario or a two-player game has no constitution to be hard.
 */
export const runModeOf = (run: RunState | undefined | null): RunMode =>
  run?.mode === "hard" ? "hard" : "normal";

/**
 * A mode off the wire, or null for a value this build does not know.
 *
 * **Refused, never defaulted** — `normalizeRuleset`'s rule and its reason: a
 * value from another build quietly read as normal would be two clients
 * deriving different games from one log. `"normal"` and absent are the same
 * answer, which is what lets a command spell the default out.
 */
export function normalizeRunMode(mode: string | undefined): RunMode | null {
  if (mode === undefined || mode === "normal") return "normal";
  if (mode === "hard") return "hard";
  return null;
}

/**
 * How many of its own turns an army has to find a way out before it lays down
 * its arms — counted by `campaign/despair.ts` (§31 T).
 *
 * Nought under hard, which is what "no grace period" is: the stamp lands and
 * the surrender is legal on the same turn (0 served is not less than 0).
 */
export const despairTurnsFor = (run: RunState | undefined | null): number =>
  runModeOf(run) === "hard" ? RULES.hardMode.despairTurns : RULES.despairTurns;

/**
 * How often the spawn clock lands a camp, in rounds (§31 T).
 *
 * Rounded, so the interval stays a whole count of rounds whatever the scale is
 * — a fractional `until` would be compared against an integer counter and the
 * clock would fire a round late or early depending on the arithmetic.
 */
export const spawnIntervalFor = (run: RunState | undefined | null): number =>
  runModeOf(run) === "hard"
    ? Math.round(RULES.spawnEvery * RULES.hardMode.spawnScale)
    : RULES.spawnEvery;

/**
 * Extra camp strength, added to the paced stage a camp is built at.
 *
 * Nought on a normal run, so `buildWave`'s arithmetic is unmoved to the
 * value and every existing seed builds the camp it always built.
 */
export const campStrengthBonus = (run: RunState | undefined | null): number =>
  runModeOf(run) === "hard" ? RULES.hardMode.strengthBonus : 0;
